/**
 * sessionProjects.ts — Client-side project/session registry (per Cognito user).
 *
 * Product model:
 *   - One user → many projects (sessionId 1, 2, 3…)
 *   - One project → one LangGraph thread (thread_id = sessionId)
 *   - Backend MexaScreen workspace will align on the same sessionId (wired later)
 *
 * Storage: localStorage keyed by username until list/create APIs exist on the backend.
 */
export const LANGGRAPH_PROJECT_ID_KEY = 'langgraph_project_id';
/** Fired when the user switches project — main.tsx reloads workflow + chat. */
export const SESSION_CHANGED_EVENT = 'mexascreen:session-changed';
/**
 * Monotonic generation bumped on every project switch.
 * In-flight loaders capture it and must discard results if it changed.
 */
let sessionLoadGeneration = 0;
export function bumpSessionLoadGeneration() {
    sessionLoadGeneration += 1;
    return sessionLoadGeneration;
}
export function getSessionLoadGeneration() {
    return sessionLoadGeneration;
}
/** True if this load still matches the active project (no switch since start). */
export function isSessionLoadCurrent(generation, sessionId) {
    if (generation !== sessionLoadGeneration)
        return false;
    if (sessionId !== undefined && resolveCurrentSessionId() !== sessionId)
        return false;
    return true;
}
function registryStorageKey(userId) {
    return `mexascreen_projects_${userId}`;
}
function saveRegistry(userId, registry) {
    localStorage.setItem(registryStorageKey(userId), JSON.stringify(registry));
}
/** Human-readable label — always the user-defined project name. */
export function formatProjectLabel(project) {
    const name = (project.baseName || `Project ${project.sessionId}`).trim();
    return name || `Project ${project.sessionId}`;
}
/** Body text for the project-switch confirmation modal. */
export function formatProjectSwitchMessage(project) {
    var _a;
    const name = formatProjectLabel(project);
    let message = `You are about to open “${name}”. ` +
        `The MexaScreen UI and assistant conversation will reload for this project.`;
    const target = (_a = project.target) === null || _a === void 0 ? void 0 : _a.trim();
    if (target) {
        message += `\n\nAssociated target: ${target}.`;
    }
    return message;
}
/** Body text for the project-delete confirmation modal. */
export function formatProjectDeleteMessage(project, isActiveProject) {
    var _a;
    const name = formatProjectLabel(project);
    let message = `“${name}” will be permanently deleted with all its receptors, peptides, dockings, ` +
        `screenings, files, and the assistant thread history (chatbot). ` +
        `This action cannot be undone.`;
    const target = (_a = project.target) === null || _a === void 0 ? void 0 : _a.trim();
    if (target) {
        message += `\n\nAssociated target: ${target}.`;
    }
    if (isActiveProject) {
        message += `\n\nYou will be switched to the first remaining project.`;
    }
    return message;
}
/** Lowest sessionId among remaining projects (default fallback after delete). */
export function getFirstProjectSessionId(userId) {
    var _a, _b;
    const projects = listProjects(userId);
    return (_b = (_a = projects[0]) === null || _a === void 0 ? void 0 : _a.sessionId) !== null && _b !== void 0 ? _b : 1;
}
/** Default name suggested when creating the next project. */
export function getDefaultNewProjectName(userId) {
    const registry = loadRegistry(userId);
    const nextId = registry.projects.length > 0
        ? Math.max(...registry.projects.map((p) => p.sessionId)) + 1
        : 1;
    return `Project ${nextId}`;
}
function normalizeProjectName(name, fallback) {
    const trimmed = name.trim().replace(/\s+/g, ' ');
    if (!trimmed)
        return fallback;
    return trimmed.slice(0, 80);
}
export function loadRegistry(userId) {
    var _a;
    const raw = localStorage.getItem(registryStorageKey(userId));
    if (raw) {
        try {
            const parsed = JSON.parse(raw);
            if (((_a = parsed === null || parsed === void 0 ? void 0 : parsed.projects) === null || _a === void 0 ? void 0 : _a.length) && typeof parsed.activeSessionId === 'number') {
                return parsed;
            }
        }
        catch (_b) {
            /* fall through to default */
        }
    }
    const initial = {
        activeSessionId: 1,
        projects: [
            {
                sessionId: 1,
                baseName: 'Project 1',
                target: null,
                createdAt: new Date().toISOString(),
            },
        ],
    };
    saveRegistry(userId, initial);
    return initial;
}
export function getActiveSessionId(userId) {
    return loadRegistry(userId).activeSessionId;
}
/** Runtime session for API paths — synced by main.tsx on login and project switch. */
export function resolveCurrentSessionId() {
    var _a;
    const rawUser = typeof localStorage !== 'undefined'
        ? localStorage.getItem('currentUser')
        : null;
    if (rawUser) {
        try {
            const username = (_a = JSON.parse(rawUser)) === null || _a === void 0 ? void 0 : _a.username;
            if (typeof username === 'string' && username) {
                return getActiveSessionId(username);
            }
        }
        catch (_b) {
            /* fall back to the in-memory bridge */
        }
    }
    if (typeof window !== 'undefined') {
        const fromWindow = window.getCurrentSessionId;
        if (typeof fromWindow === 'function') {
            return fromWindow();
        }
    }
    return 1;
}
export function getProjectBySessionId(userId, sessionId) {
    return loadRegistry(userId).projects.find((p) => p.sessionId === sessionId);
}
export function listProjects(userId) {
    return [...loadRegistry(userId).projects].sort((a, b) => a.sessionId - b.sessionId);
}
/** Keep LangGraph thread_id in sync with the active MexaScreen session. */
export function syncLangGraphProjectId(sessionId) {
    localStorage.setItem(LANGGRAPH_PROJECT_ID_KEY, String(sessionId));
}
export function setActiveSessionId(userId, sessionId) {
    const registry = loadRegistry(userId);
    if (!registry.projects.some((p) => p.sessionId === sessionId)) {
        console.warn(`[sessionProjects] Unknown sessionId ${sessionId} for user ${userId}`);
        return;
    }
    if (registry.activeSessionId !== sessionId) {
        bumpSessionLoadGeneration();
    }
    registry.activeSessionId = sessionId;
    saveRegistry(userId, registry);
    syncLangGraphProjectId(sessionId);
}
/** Create the next project/session for this user and make it active. */
export function createNewProject(userId, baseName) {
    const registry = loadRegistry(userId);
    const nextId = registry.projects.length > 0
        ? Math.max(...registry.projects.map((p) => p.sessionId)) + 1
        : 1;
    const defaultName = `Project ${nextId}`;
    const project = {
        sessionId: nextId,
        baseName: normalizeProjectName(baseName !== null && baseName !== void 0 ? baseName : defaultName, defaultName),
        target: null,
        createdAt: new Date().toISOString(),
    };
    registry.projects.push(project);
    registry.activeSessionId = nextId;
    bumpSessionLoadGeneration();
    saveRegistry(userId, registry);
    syncLangGraphProjectId(nextId);
    return project;
}
/** Store orchestrator target as metadata (does not change the project display name). */
export function updateProjectTarget(userId, sessionId, target) {
    const trimmed = target.trim();
    if (!trimmed)
        return false;
    const registry = loadRegistry(userId);
    const project = registry.projects.find((p) => p.sessionId === sessionId);
    if (!project || project.target === trimmed)
        return false;
    project.target = trimmed;
    saveRegistry(userId, registry);
    return true;
}
export function clearUserProjects(userId) {
    localStorage.removeItem(registryStorageKey(userId));
}
export function dispatchSessionChanged(userId, sessionId) {
    // Ensure loaders started before setActiveSessionId still see a generation bump.
    bumpSessionLoadGeneration();
    window.dispatchEvent(new CustomEvent(SESSION_CHANGED_EVENT, {
        detail: { userId, sessionId },
    }));
}
/** Register a session returned by POST /frontend/sessions. */
export function adoptBackendSession(userId, sessionId, baseName) {
    const registry = loadRegistry(userId);
    const name = baseName.trim() || `Project ${sessionId}`;
    const existing = registry.projects.find((p) => p.sessionId === sessionId);
    if (existing) {
        existing.baseName = name;
    }
    else {
        registry.projects.push({
            sessionId,
            baseName: name,
            target: null,
            createdAt: new Date().toISOString(),
        });
    }
    registry.activeSessionId = sessionId;
    saveRegistry(userId, registry);
    syncLangGraphProjectId(sessionId);
}
/** Sync local registry with backend list — drops orphan local-only sessions (e.g. pre-backend Project 5). */
export function syncRegistryFromBackend(userId, sessions) {
    var _a, _b;
    if (!sessions.length)
        return;
    const registry = loadRegistry(userId);
    const previous = registry.projects;
    registry.projects = sessions
        .slice()
        .sort((a, b) => a.sessionId - b.sessionId)
        .map((s) => {
        var _a, _b, _c;
        const local = previous.find((p) => p.sessionId === s.sessionId);
        return {
            sessionId: s.sessionId,
            baseName: s.projectName || `Project ${s.sessionId}`,
            target: (_a = local === null || local === void 0 ? void 0 : local.target) !== null && _a !== void 0 ? _a : null,
            createdAt: (_c = (_b = s.startedAt) !== null && _b !== void 0 ? _b : local === null || local === void 0 ? void 0 : local.createdAt) !== null && _c !== void 0 ? _c : new Date().toISOString(),
        };
    });
    const backendIds = new Set(sessions.map((s) => s.sessionId));
    if (!backendIds.has(registry.activeSessionId)) {
        registry.activeSessionId = (_b = (_a = registry.projects[0]) === null || _a === void 0 ? void 0 : _a.sessionId) !== null && _b !== void 0 ? _b : 1;
        syncLangGraphProjectId(registry.activeSessionId);
    }
    saveRegistry(userId, registry);
}
/** @deprecated Use syncRegistryFromBackend — kept as alias for callers. */
export function mergeBackendSessions(userId, sessions) {
    syncRegistryFromBackend(userId, sessions);
}
/** Remove a project from the local registry after backend deletion. */
export function removeProjectFromRegistry(userId, sessionId, fallbackSessionId) {
    var _a, _b, _c;
    const registry = loadRegistry(userId);
    const wasActive = registry.activeSessionId === sessionId;
    registry.projects = registry.projects.filter((p) => p.sessionId !== sessionId);
    if (registry.projects.length === 0) {
        registry.projects = [{
                sessionId: 1,
                baseName: 'Project 1',
                target: null,
                createdAt: new Date().toISOString(),
            }];
    }
    const sorted = [...registry.projects].sort((a, b) => a.sessionId - b.sessionId);
    const nextActive = fallbackSessionId !== null && fallbackSessionId !== void 0 ? fallbackSessionId : (wasActive ? (_a = sorted[0]) === null || _a === void 0 ? void 0 : _a.sessionId : registry.activeSessionId);
    if (!sorted.some((p) => p.sessionId === nextActive)) {
        registry.activeSessionId = (_c = (_b = sorted[0]) === null || _b === void 0 ? void 0 : _b.sessionId) !== null && _c !== void 0 ? _c : 1;
    }
    else {
        registry.activeSessionId = nextActive;
    }
    syncLangGraphProjectId(registry.activeSessionId);
    saveRegistry(userId, registry);
}
